/**
 * OracleInsightsTab.tsx - Top-level tab for Oracle Insights visualization
 *
 * Displays the Reasoning Oracle's inference chains, confidence scoring,
 * and causal explanations in an interactive visual format.
 *
 * Reference: wobbly-nibbling-chipmunk.md Part 15
 * Reference: reasoning-oracle-prototype-selection.md Section 8
 */

import { useEffect, useCallback, useContext, useState } from 'react'
import { useAppSelector, useAppDispatch } from '@state/hooks'
import {
  selectOracleResponse,
  selectOracleLoading,
  selectOracleError,
  selectOracleUI,
  selectOracleHistory,
  selectSelectedHistoryIndex,
  setExplanationTab,
  selectChain,
  setOracleResponses,
  selectHistoryEntry,
  cacheResponses,
  setLoading,
  setError,
  clearOracleResponse,
} from '@state/slices/oracleSlice'
import { TabNavigationContext, useIncomingNavigationContext, CenterTab } from '@contexts/TabNavigationContext'
import type { ExplanationTab } from '@models/oracle'
import { navigateToGraphForNode } from '@services/crossTabNavigation'
import { scrollToSection } from '@utils/crossTabNavigation'
import {
  InferenceChainViewer,
  OracleHeader,
  ConfidencePanel,
  ExplanationPanel,
  BSCFlowDiagram,
  GapAlertPanel,
  OracleIntroPanel,
  PillarInferenceView,
  OracleGuidancePanel,
  StrategicNarrativeView,
} from '@components/oracle'
import OracleHistoryBar from '@components/oracle/OracleHistoryBar'
import IVPGroundingBadge from '@components/oracle/IVPGroundingBadge'
import TierHeader from '@components/oracle/TierHeader'
import DetailsAccordion, { AccordionSection } from '@components/oracle/DetailsAccordion'
import AdvancedDrawer from '@components/oracle/AdvancedDrawer'
import { fetchOracleInsights, OracleApiError } from '@services/oracleApi'
import { store } from '@state/store'
import { resolveOracleSource } from './oracleSourceResolver'
import './OracleInsightsTab.css'

function OracleInsightsTab() {
  const dispatch = useAppDispatch()
  const { activeConversationId } = useAppSelector((state) => state.conversations)
  const tabNavigation = useContext(TabNavigationContext)

  // Chain pane fullscreen state — tab-level fullscreen removed for cleaner hero
  const [isChainFullscreen, setIsChainFullscreen] = useState(false)

  // Strategic narrative mode state (Phase 4)
  const [isStrategicMode, setIsStrategicMode] = useState(false)

  // Incoming navigation context (Phase 5)
  const { incomingContext, clearContext } = useIncomingNavigationContext('oracle')

  // Handle incoming navigation context
  useEffect(() => {
    if (incomingContext) {
      console.log('[OracleInsights] Received navigation context:', incomingContext)

      // Handle pillar filter
      if (incomingContext.filterByPillar) {
        console.log('[OracleInsights] Filter by pillar:', incomingContext.filterByPillar)
        // Future: Set filter state to show only chains related to this pillar
      }

      // Handle node filter
      if (incomingContext.filterByNode) {
        console.log('[OracleInsights] Filter by node:', incomingContext.filterByNode)
        // Future: Set filter state to show only chains containing this node
      }

      // Handle chain highlight
      if (incomingContext.highlightChainIndex !== undefined) {
        dispatch(selectChain(incomingContext.highlightChainIndex))
      }

      // Clear context after processing
      clearContext()
    }
  }, [incomingContext, clearContext, dispatch])

  // Toggle chain pane fullscreen
  const toggleChainFullscreen = useCallback(() => {
    setIsChainFullscreen((prev) => !prev)
  }, [])

  // Toggle strategic narrative mode
  const toggleStrategicMode = useCallback(() => {
    setIsStrategicMode((prev) => !prev)
  }, [])

  // Handle Escape key — exit chain-pane fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isChainFullscreen) {
        setIsChainFullscreen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isChainFullscreen])

  // Oracle state
  const response = useAppSelector(selectOracleResponse)
  const loading = useAppSelector(selectOracleLoading)
  const error = useAppSelector(selectOracleError)
  const ui = useAppSelector(selectOracleUI)
  const responseHistory = useAppSelector(selectOracleHistory)
  const selectedHistoryIndex = useAppSelector(selectSelectedHistoryIndex)

  // Fetch Oracle insights when conversation changes (3-tier lookup):
  //   Tier 1: Redux cache (oracleSlice.responseCache) — synchronous
  //   Tier 2: Hydrated conversation data (oracleInsightsData) — synchronous
  //   Tier 3: API fetch with DB fallback — async, last resort
  //
  // The Quick-mode gate runs AFTER Tier 1+2 so a conversation whose latest run
  // was Quick still surfaces Oracle data persisted by prior Strategy runs.
  //
  // None of these paths emit a "fresh" signal — they all hydrate existing data,
  // so auto-switch (which listens to lastFreshAt) stays dormant here. Fresh
  // signals are reserved for actual /oracle/analyze completions.
  //
  // CRITICAL: deps must be [activeConversationId, dispatch] ONLY — do NOT add
  // conversations or oracleCache, as those change on every Redux update during
  // strategy planning and would trigger unnecessary API calls.
  useEffect(() => {
    if (!activeConversationId) return

    // Clear stale data first — OracleInsightsTab is the sole owner of Oracle state
    dispatch(clearOracleResponse())

    const source = resolveOracleSource(store.getState() as any, activeConversationId)

    if (source.kind === 'cache') {
      dispatch(setOracleResponses(source.responses))
      return
    }

    if (source.kind === 'hydrated') {
      dispatch(setOracleResponses(source.responses))
      dispatch(cacheResponses({ conversationId: activeConversationId, responses: source.responses }))
      return
    }

    if (source.kind === 'skip-quick') {
      return // No prior Oracle data + Quick mode — show empty state, skip API
    }

    // Tier 3: Fetch from API (backend has DB fallback for server restarts)
    const loadInsights = async () => {
      dispatch(setLoading(true))
      try {
        const result = await fetchOracleInsights(activeConversationId)
        dispatch(setOracleResponses(result.responses))
        dispatch(cacheResponses({ conversationId: activeConversationId, responses: result.responses }))
      } catch (err) {
        // 404 = no oracle data yet (new conversation) — clear stale state and show empty state
        if (err instanceof OracleApiError && err.statusCode === 404) {
          dispatch(clearOracleResponse())
          return
        }
        dispatch(
          setError(err instanceof Error ? err.message : 'Failed to load Oracle insights')
        )
      }
    }

    loadInsights()
  }, [activeConversationId, dispatch])

  // Handle explanation tab change
  const handleTabChange = useCallback(
    (tab: ExplanationTab) => {
      dispatch(setExplanationTab(tab))
    },
    [dispatch]
  )

  // Handle chain selection
  const handleSelectChain = useCallback(
    (index: number) => {
      dispatch(selectChain(index))
    },
    [dispatch]
  )

  // Handle cross-tab navigation to Business Graph (with context)
  const handleNodeDoubleClick = useCallback(
    (nodeId: string) => {
      console.log('[OracleInsights] Navigate to Business Graph for node:', nodeId)
      if (tabNavigation?.navigateToTab) {
        // Use contextual navigation to select and center on the node
        navigateToGraphForNode(tabNavigation.navigateToTab, nodeId)
      }
    },
    [tabNavigation]
  )

  // Handle highlighting a node in the Business Graph (from Guidance Panel)
  const handleHighlightInGraph = useCallback(
    (nodeId: string) => {
      console.log('[OracleInsights] Highlight in Business Graph:', nodeId)
      if (tabNavigation?.navigateToTab) {
        navigateToGraphForNode(tabNavigation.navigateToTab, nodeId)
      }
    },
    [tabNavigation]
  )

  // Handle gap click
  const handleGapClick = useCallback((gap: string) => {
    console.log('[OracleInsights] Gap clicked:', gap)
    // Future: Could open a modal to suggest ontology addition
  }, [])

  // Handle history entry selection
  const handleSelectHistory = useCallback(
    (index: number) => {
      dispatch(selectHistoryEntry(index))
    },
    [dispatch]
  )

  // Handle cross-tab navigation from OracleIntroPanel
  const handleNavigateToTab = useCallback(
    (tab: CenterTab) => {
      console.log('[OracleInsights] Navigate to tab:', tab)
      if (tabNavigation?.navigateToTab) {
        tabNavigation.navigateToTab(tab)
      }
    },
    [tabNavigation]
  )

  // Handle section-specific navigation to Strategy Workspace
  // Uses direct scrollToSection after tab switch because MainLayout provides
  // a minimal TabNavigationContext (navigateToTab only, no contextual methods).
  // Tabs are conditionally rendered, so DOM elements exist after remount + render.
  const handleNavigateToWorkspaceSection = useCallback(
    (section: 'mece' | 'strategy-map') => {
      console.log('[OracleInsights] Navigate to workspace section:', section)
      if (tabNavigation?.navigateToTab) {
        tabNavigation.navigateToTab('workspace')
        // After tab switch triggers workspace remount + render, scroll to section
        setTimeout(() => {
          scrollToSection(section + '-section')
        }, 300)
      }
    },
    [tabNavigation]
  )

  // Empty state - no conversation selected
  if (!activeConversationId) {
    return (
      <div className="oracle-insights-tab-empty">
        <div className="empty-message">
          <div className="oracle-icon">&#x1F52E;</div>
          <h2>Causal Insights</h2>
          <p>Strategy that explains itself — visible causal chains, defensibly grounded.</p>
          <p className="hint">Select a conversation to view inference chains</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="oracle-insights-tab-loading">
        <div className="loading-spinner" />
        <p>Analyzing causal patterns...</p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="oracle-insights-tab-error">
        <div className="error-icon">&#x26A0;</div>
        <h3>Causal Insights — error</h3>
        <p>{error}</p>
      </div>
    )
  }

  // No Oracle data yet
  if (!response) {
    return (
      <div className="oracle-insights-tab-empty">
        <div className="empty-message">
          <div className="oracle-icon">&#x1F52E;</div>
          <h2>Causal Insights</h2>
          <p>Causeway's causal reasoning will appear here after running Strategy Planning mode.</p>
          <p className="hint">
            Use Strategy Planning mode (Express, Guided, or Explore) to generate causal insights.
            Quick mode responses do not trigger causal-chain analysis.
          </p>
        </div>
      </div>
    )
  }

  // Main content with Oracle data — 3-tier disclosure layout
  const crossPerspectiveCount = (response.classification.bsc_perspectives || []).length
  const confidenceLabel = response.confidence.level

  return (
    <div className="oracle-insights-tab">
      <OracleIntroPanel
        response={response}
        onNavigateToTab={handleNavigateToTab}
        onNavigateToWorkspaceSection={handleNavigateToWorkspaceSection}
        selectedHistoryIndex={selectedHistoryIndex}
        totalHistoryCount={responseHistory.length}
        onViewLatest={() => handleSelectHistory(0)}
      />

      {responseHistory.length > 1 && (
        <OracleHistoryBar
          responses={responseHistory}
          selectedIndex={selectedHistoryIndex}
          onSelectEntry={handleSelectHistory}
        />
      )}

      {/* TIER 1 — SUMMARY (the 5-second defensible answer) */}
      <TierHeader
        title="Summary"
        subtitle="The 5-second answer, defensibly grounded."
        tooltip="Your defensible 5-second story. IVP grounding tag, query, causal chain, and confidence summary — everything needed to explain what Causeway found and how it grounded the answer. The view you can put in front of a client without scrolling."
      />

      <div className="oracle-hero-card">
        <IVPGroundingBadge />

        <div className="oracle-header-wrapper">
          <OracleHeader response={response} />
        </div>

        {/* Plain-English explanation inside Summary — same component as in
            Audit trail but rendered in always-on strategic mode. The toggle
            buttons are CSS-hidden inside the hero (.oracle-hero-card scope)
            so the narrative is always the dominant content here. The
            toggleable version still lives in the Audit-trail drawer below. */}
        <div className="oracle-hero-narrative">
          <StrategicNarrativeView
            response={response}
            isStrategicMode={true}
            onToggleMode={() => { /* no-op inside Summary */ }}
          />
        </div>

        <div className={`inference-chain-panel ${isChainFullscreen ? 'chain-fullscreen' : ''}`}>
          <div className="panel-header">
            <h4>Causal Chain</h4>
            <span className="chain-count">
              {response.inference_chains.length} chain{response.inference_chains.length !== 1 ? 's' : ''}
            </span>
            <button
              className={`chain-fullscreen-toggle ${isChainFullscreen ? 'active' : ''}`}
              onClick={toggleChainFullscreen}
              title={isChainFullscreen ? 'Exit pane fullscreen (Esc)' : 'Expand chain viewer'}
              aria-label={isChainFullscreen ? 'Exit pane fullscreen' : 'Expand chain viewer'}
            >
              {isChainFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              )}
            </button>
          </div>

          <div className="chain-viewer-container">
            {response.inference_chains.length > 0 ? (
              <InferenceChainViewer
                chains={response.inference_chains}
                selectedChainIndex={ui.selectedChainIndex}
                onSelectChain={handleSelectChain}
                onNodeDoubleClick={handleNodeDoubleClick}
                chainExplanations={response.explanation?.chain_explanations}
                nodeMetrics={response.node_metrics}
                nodeKsas={response.node_ksas}
                nodeRefDocs={response.node_ref_docs}
              />
            ) : (
              <div className="no-chains-message">
                <div className="icon">🔗</div>
                <h3>No Inference Chains</h3>
                <p>Run a strategic query to generate causal inferences</p>
              </div>
            )}
          </div>
        </div>

        <div className={`oracle-confidence-pill confidence-${confidenceLabel.toLowerCase()}`}>
          {confidenceLabel === 'HIGH' || confidenceLabel === 'MEDIUM'
            ? `${confidenceLabel.charAt(0) + confidenceLabel.slice(1).toLowerCase()} confidence`
            : `${confidenceLabel} confidence`}
          <span className="oracle-confidence-pill-pct"> · {Math.round(response.confidence.score * 100)}%</span>
        </div>
      </div>

      {/* TIER 2 — REASONING (the why behind each chain) */}
      <TierHeader
        title="Reasoning"
        subtitle="Expand a section to see the why behind each chain."
        tooltip="The why of the why. Confidence breakdown shows per-chain evidence strength. Explanation surfaces per-chain narratives and source quotes. BSC Flow shows how strategic perspectives connect. Gaps tells you where Causeway couldn't ground a claim — the honest limits of the analysis. Open these when defending the summary in front of a client or sharpening a follow-up question."
      />

      <DetailsAccordion>
        <AccordionSection title="Confidence breakdown" badge={confidenceLabel}>
          <ConfidencePanel confidence={response.confidence} />
        </AccordionSection>
        <AccordionSection title="Explanation" badge={`${response.inference_chains.length} chains`}>
          <ExplanationPanel
            explanation={response.explanation}
            activeTab={ui.activeExplanationTab}
            onTabChange={handleTabChange}
          />
        </AccordionSection>
        <AccordionSection title="BSC Flow" badge={`${crossPerspectiveCount} active`}>
          <BSCFlowDiagram
            bscFlow={response.explanation.bsc_flow}
            activePerspectives={response.classification.bsc_perspectives}
          />
        </AccordionSection>
        <AccordionSection title="Gaps & Limitations" badge={`${response.explanation.gaps_identified?.length || 0} gaps`}>
          <GapAlertPanel
            gaps={response.explanation.gaps_identified}
            limitations={response.explanation.limitations}
            onGapClick={handleGapClick}
          />
        </AccordionSection>
      </DetailsAccordion>

      {/* TIER 3 — AUDIT TRAIL (full reasoning detail for client deliverables) */}
      <TierHeader
        title="Audit trail"
        subtitle="Power-user reasoning detail. Open when preparing a deliverable."
        tooltip="The full audit trail. Pillar Inference shows how each strategic pillar was activated and weighted. Strategic Narrative is the long-form story across all chains, useful as the spine of a written report. Schemas Activated tells you which structured templates the reasoning engine recognized. Guidance Panel is the actionable next-step recommendation. Open when packaging insight into a client deliverable, not when skimming."
      />

      <AdvancedDrawer>
        <PillarInferenceView
          inferenceChains={response.inference_chains}
          confidence={response.confidence}
          onNavigateToTab={handleNavigateToTab}
        />
        <OracleGuidancePanel
          confidence={response.confidence}
          inferenceChains={response.inference_chains}
          gaps={response.explanation.gaps_identified}
          onNavigateToTab={handleNavigateToTab}
          onHighlightInGraph={handleHighlightInGraph}
        />
        <StrategicNarrativeView
          response={response}
          isStrategicMode={isStrategicMode}
          onToggleMode={toggleStrategicMode}
        />
        {/* Schemas Activated chip strip removed — engineering codenames had
            zero advisor value and the same schemas appear human-readably in
            Reasoning → Explanation accordion. */}
      </AdvancedDrawer>
    </div>
  )
}

export default OracleInsightsTab
